import type { ReactNode } from 'react';
import { BrandMark } from '@/components/BrandMark';
import { LanguageToggle } from '@/components/theme/LanguageToggle';

/**
 * BootFrame — единственная картинка ожидания на пути «сплеш → вход → приложение».
 *
 * Замер первого запуска (docs/plans/2026-09-09-first-run-shell-continuity.md):
 * от выбора аккаунта до устоявшегося дашборда экран менялся целиком 13 раз за
 * ~40 секунд, а новую информацию несли три смены из тринадцати. Остальные
 * десять — одно и то же ожидание в разном оформлении: сплеш, потом другая
 * марка в другом месте, потом скелет приложения, потом он же заново.
 *
 * Отсюда два правила этого компонента.
 *
 * **Марка стоит на фиксированном отступе сверху**, а не центрируется вместе с
 * содержимым. Отступ приходит из токена системы `--spacing-boot-brand-top`
 * (`pt-boot-brand-top`), и тем же значением пользуется загрузочный сплеш —
 * поэтому переход «сплеш → React» не двигает
 * марку ни на пиксель. Центрирование этого дать не может: под формой Clerk
 * марка уезжает вверх, под одной строкой статуса — вниз, и каждая стадия
 * читается как новая страница.
 *
 * **Меняется только то, что действительно изменилось.** `headline`/`subtitle`
 * — на настоящей смене состояния (связываем → подключено → ошибка), `children`
 * — строка статуса или блок действия. Экран не пересобирается ради строки.
 *
 * Прокручивается ВНУТРЕННИЙ слой, а не сам кадр: у `body` в index.css стоит
 * `overflow: hidden`, и без прокрутки форма Clerk на невысоком окне обрезалась
 * бы вместе с кнопкой отправки. Полоса ожидания и переключатель языка при этом
 * остаются снаружи прокрутки — иначе они уезжали бы вверх вместе с
 * содержимым, и признак работы пропадал бы посреди ожидания.
 *
 * Земля — `bg-bg`, та же, что у `body` в index.css и у самого приложения.
 * Прежде тут был `bg-bg-void`, и скачок фона никуда не девался — он лишь
 * переезжал со стыка «сплеш → React» на стык «кадр ожидания → приложение»
 * (#fafafa против #ffffff в светлой Meridian).
 */
export function BootFrame({
  headline,
  subtitle,
  busy = false,
  children,
}: {
  headline: string;
  subtitle?: string;
  /**
   * Идёт ли работа прямо сейчас. Управляет полосой ожидания — той же, что на
   * сплеше (`.boot-progress` объявлен в index.html, чтобы определение было
   * одно). Прежде полоса жила только на сплеше и пропадала на handoff'е:
   * человек, у которого привязка ещё шла, видел, как признак работы исчезает.
   *
   * Обратное так же важно: на форме входа и на терминальных экранах ход за
   * человеком, и движущаяся полоса там сообщала бы о работе, которой нет.
   * Тот же флаг включает сигнал на марке — по той же причине.
   */
  busy?: boolean;
  children?: ReactNode;
}) {
  return (
    // Без `aria-busy` на этом узле — намеренно. Он объявляет ветку «в
    // процессе изменения», и живая область НИЖЕ оказалась бы внутри неё: по
    // ARIA вспомогательные технологии вправе придержать изменения внутри
    // занятого поддерева до его освобождения. То есть флаг глушил бы ровно те
    // объявления стадий, ради которых живая область и добавлена, — а на пути
    // `/link-portal` он не снимается вовсе, пока идёт привязка. Что работа
    // идёт, говорит сам текст стадии; полоса это дублирует глазами.
    <div className="relative h-screen bg-bg">
      {busy && (
        // Декоративна намеренно. `role="progressbar"` без `aria-valuenow`
        // ничего не сообщает во времени — скринридер прочитает его только
        // если на него перейти, чего на экране загрузки не делают, — зато
        // `aria-label` сделал бы заголовок вторым доступным именем. Смену
        // стадий объявляет живая область ниже, где меняется сам текст.
        <div className="boot-progress" aria-hidden="true" data-testid="boot-progress" />
      )}
      <div className="absolute right-4 top-4 z-10">
        <LanguageToggle showLabel />
      </div>

      {/* `scrollbar-gutter: stable` — не косметика: полоса прокрутки появляется
          на стадии с формой Clerk и пропадает на стадии с одной строкой, а
          вместе с ней на ~7px ездит по горизонтали центрированная марка. Весь
          этот экран написан ради того, чтобы марка не двигалась. Место под
          полосу резервируется всегда, и ширина содержимого одна на всех
          стадиях. */}
      <div className="flex h-full flex-col items-center overflow-y-auto [scrollbar-gutter:stable] px-4 pb-12 pt-boot-brand-top">
        {/* Живая область на тексте, а не на полоске: объявлять надо то, что
            МЕНЯЕТСЯ. Без неё скринридер читал заголовок один раз и дальше
            молчал всё ожидание, сколько бы стадий оно ни сменило. */}
        <div
          role="status"
          aria-live="polite"
          className="flex flex-col items-center gap-3 text-center"
        >
          <BrandMark signal={busy} />
          <h1 className="text-2xl font-semibold text-fg">{headline}</h1>
          {subtitle && <p className="max-w-sm text-sm text-fg-muted">{subtitle}</p>}
        </div>

        {children && (
          <div className="mt-8 flex w-full flex-col items-center">{children}</div>
        )}
      </div>
    </div>
  );
}
